import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { ArrowLeft, Languages, CheckCircle, AlertCircle, Loader2, Copy, Check } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const TranslationTestPage = () => {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [languages, setLanguages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [testResults, setTestResults] = useState(null);
  const [copiedLang, setCopiedLang] = useState(null);
  
  const [testText, setTestText] = useState(
    "Your leadership assessment reveals exceptional strategic thinking capabilities. With a score of 87% in decision-making and 92% in team collaboration, you demonstrate the qualities of an influential leader who can inspire and guide others effectively."
  );
  const [selectedLanguages, setSelectedLanguages] = useState(['hi', 'ta', 'ar']);

  useEffect(() => {
    const token = localStorage.getItem('token');
    const userData = localStorage.getItem('user');
    
    if (!token || !userData) {
      navigate('/auth');
      return;
    }

    const parsedUser = JSON.parse(userData);
    if (!parsedUser.is_admin) {
      navigate('/dashboard');
      return;
    }
    
    setUser(parsedUser);
    fetchLanguages(token);
  }, [navigate]);

  const fetchLanguages = async (token) => {
    try {
      const response = await fetch(`${API_URL}/api/translate/languages`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setLanguages(data.languages || []);
      }
    } catch (error) {
      console.error('Error fetching languages:', error);
    }
  };

  const handleLanguageToggle = (langCode) => {
    setSelectedLanguages(prev => {
      if (prev.includes(langCode)) {
        return prev.filter(l => l !== langCode);
      } else {
        return [...prev, langCode];
      }
    });
  };

  const runTranslationTest = async () => {
    if (!testText.trim() || selectedLanguages.length === 0) return;
    
    setLoading(true);
    setTestResults(null);
    
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/translate/test`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          text: testText,
          languages: selectedLanguages
        })
      });

      if (response.ok) {
        const data = await response.json();
        setTestResults(data);
      } else {
        const error = await response.json();
        alert(`Error: ${error.detail || 'Translation test failed'}`);
      }
    } catch (error) {
      console.error('Error running translation test:', error);
      alert('Failed to run translation test');
    } finally {
      setLoading(false);
    }
  };

  const copyToClipboard = (text, langCode) => {
    navigator.clipboard.writeText(text);
    setCopiedLang(langCode);
    setTimeout(() => setCopiedLang(null), 2000);
  };

  const getQualityColor = (level) => {
    switch (level) {
      case 'perfect': return 'text-emerald-500';
      case 'excellent': return 'text-green-500';
      case 'good': return 'text-blue-500';
      case 'needs_review': return 'text-yellow-500';
      case 'rejected': return 'text-red-500';
      default: return 'text-gray-500';
    }
  };

  const getQualityBg = (level) => {
    switch (level) {
      case 'perfect': return 'bg-emerald-500/10 border-emerald-500/30';
      case 'excellent': return 'bg-green-500/10 border-green-500/30';
      case 'good': return 'bg-blue-500/10 border-blue-500/30';
      case 'needs_review': return 'bg-yellow-500/10 border-yellow-500/30';
      case 'rejected': return 'bg-red-500/10 border-red-500/30';
      default: return 'bg-gray-500/10 border-gray-500/30';
    }
  };

  if (!user) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-amber-500" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900">
      {/* Header */}
      <header className="border-b border-gray-800 bg-gray-900/80 backdrop-blur-lg sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button 
              variant="ghost" 
              size="sm"
              onClick={() => navigate('/admin')}
              className="text-gray-400 hover:text-white"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Admin Dashboard
            </Button>
          </div>
          <div className="flex items-center gap-2">
            <Languages className="w-5 h-5 text-amber-500" />
            <span className="text-white font-medium">Translation Quality Tester</span>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 py-8 space-y-8">
        {/* Introduction */}
        <Card className="bg-gray-800/50 border-gray-700">
          <CardHeader>
            <CardTitle className="text-2xl text-white flex items-center gap-3">
              <Languages className="w-8 h-8 text-amber-500" />
              Enhanced Translation Quality Tester
            </CardTitle>
            <CardDescription className="text-gray-400 text-base">
              Test the 4-pass multi-system translation engine. This uses GPT-5.2 with language-specific 
              expert personas to achieve native-quality translations with zero grammatical errors.
            </CardDescription>
          </CardHeader>
        </Card>

        {/* Test Input */}
        <Card className="bg-gray-800/50 border-gray-700">
          <CardHeader>
            <CardTitle className="text-lg text-white">Source Text (English)</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <textarea
              value={testText}
              onChange={(e) => setTestText(e.target.value)}
              placeholder="Enter text to translate..."
              className="w-full h-32 px-4 py-3 bg-gray-900 border border-gray-700 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-amber-500/50 resize-none"
            />
            
            {/* Language Selection */}
            <div className="space-y-3">
              <label className="text-sm font-medium text-gray-300">Select Languages to Test:</label>
              <div className="flex flex-wrap gap-2">
                {languages.map((lang) => (
                  <button
                    key={lang.code}
                    onClick={() => handleLanguageToggle(lang.code)}
                    className={`px-3 py-1.5 rounded-full text-sm font-medium transition-all ${
                      selectedLanguages.includes(lang.code)
                        ? 'bg-amber-500 text-black'
                        : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                    }`}
                  >
                    {lang.native_name} ({lang.code})
                    {lang.rtl && <span className="ml-1 text-xs opacity-60">RTL</span>}
                  </button>
                ))}
              </div>
              <p className="text-xs text-gray-500">
                Selected: {selectedLanguages.length} language(s) - {selectedLanguages.join(', ')}
              </p>
            </div>

            <Button
              onClick={runTranslationTest}
              disabled={loading || !testText.trim() || selectedLanguages.length === 0}
              className="w-full bg-amber-500 hover:bg-amber-600 text-black font-semibold"
              data-testid="run-translation-test-btn"
            >
              {loading ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Running 4-Pass Translation... (This may take a moment)
                </>
              ) : (
                <>
                  <Languages className="w-4 h-4 mr-2" />
                  Run Translation Test
                </>
              )}
            </Button>
          </CardContent>
        </Card>

        {/* Results */}
        {testResults && (
          <div className="space-y-6">
            {/* Overall Score */}
            <Card className={`border ${getQualityBg(testResults.overall_quality_score >= 90 ? 'excellent' : testResults.overall_quality_score >= 80 ? 'good' : 'needs_review')}`}>
              <CardContent className="py-6">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="text-lg font-semibold text-white">Overall Quality Score</h3>
                    <p className="text-gray-400">Across {testResults.languages_tested} languages</p>
                  </div>
                  <div className="text-right">
                    <div className={`text-4xl font-bold ${getQualityColor(testResults.overall_quality_score >= 90 ? 'excellent' : testResults.overall_quality_score >= 80 ? 'good' : 'needs_review')}`}>
                      {testResults.overall_quality_score?.toFixed(1)}%
                    </div>
                    <div className="text-sm text-gray-400">
                      {testResults.overall_quality_score >= 95 ? 'PERFECT' :
                       testResults.overall_quality_score >= 90 ? 'EXCELLENT' :
                       testResults.overall_quality_score >= 80 ? 'GOOD' :
                       testResults.overall_quality_score >= 70 ? 'NEEDS REVIEW' : 'REJECTED'}
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Individual Results */}
            <div className="grid gap-4">
              {Object.entries(testResults.translations).map(([langCode, result]) => (
                <Card key={langCode} className={`border ${getQualityBg(result.quality_level)}`}>
                  <CardContent className="py-4">
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-2">
                          <span className="text-lg font-semibold text-white">
                            {result.language_name}
                          </span>
                          <span className="text-sm text-gray-500">({langCode})</span>
                          <div className={`flex items-center gap-1 text-sm ${getQualityColor(result.quality_level)}`}>
                            {result.quality_level === 'perfect' || result.quality_level === 'excellent' ? (
                              <CheckCircle className="w-4 h-4" />
                            ) : (
                              <AlertCircle className="w-4 h-4" />
                            )}
                            {result.quality_score}% - {result.quality_level?.toUpperCase()}
                          </div>
                        </div>
                        
                        <div 
                          className={`p-4 bg-gray-900/50 rounded-lg text-white leading-relaxed ${
                            ['ar', 'ur'].includes(langCode) ? 'text-right' : ''
                          }`}
                          dir={['ar', 'ur'].includes(langCode) ? 'rtl' : 'ltr'}
                        >
                          {result.translated}
                        </div>
                        
                        <div className="mt-2 text-xs text-gray-500">
                          4-pass translation completed | Passes: {result.passes_completed}
                        </div>
                      </div>
                      
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => copyToClipboard(result.translated, langCode)}
                        className="text-gray-400 hover:text-white"
                      >
                        {copiedLang === langCode ? (
                          <Check className="w-4 h-4 text-green-500" />
                        ) : (
                          <Copy className="w-4 h-4" />
                        )}
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>

            {/* Quality Legend */}
            <Card className="bg-gray-800/30 border-gray-700">
              <CardContent className="py-4">
                <h4 className="text-sm font-medium text-gray-400 mb-3">Quality Levels:</h4>
                <div className="flex flex-wrap gap-4 text-sm">
                  <div className="flex items-center gap-2">
                    <div className="w-3 h-3 rounded-full bg-emerald-500"></div>
                    <span className="text-gray-300">Perfect (95-100%)</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-3 h-3 rounded-full bg-green-500"></div>
                    <span className="text-gray-300">Excellent (90-94%)</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-3 h-3 rounded-full bg-blue-500"></div>
                    <span className="text-gray-300">Good (80-89%)</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-3 h-3 rounded-full bg-yellow-500"></div>
                    <span className="text-gray-300">Needs Review (70-79%)</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-3 h-3 rounded-full bg-red-500"></div>
                    <span className="text-gray-300">Rejected (&lt;70%)</span>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        )}
      </main>
    </div>
  );
};

export default TranslationTestPage;
